<template>
  <section>
    <nav>
      <ul :class="[$style.navList, customNavStyle]">
        <li
          v-for="(tab, index) in tabs"
          :key="index"
          :class="{ [customActiveTabStyle]: isActiveTab(index) }"
          data-test-id="TabList_Tab"
          @click.prevent="toggleActiveTab(index)"
        >
          <a
            :class="[$style.tabHeader, getTabStyle(index), customTabStyle]"
            href="javascript:void(0);"
          >
            <span
              v-if="tab.icon && (tab.iconPosition === 'left' || !tab.iconPosition) "
              :style="tab.iconStyle"
            >
              <Icon
                :icon="tab.icon"
              />
              {{ tab.iconText }}
            </span>
            <span>
              {{ tab.title }}
            </span>
            <span
              v-if="tab.icon && tab.iconPosition === 'right'"
              :style="tab.iconStyle"
            >
              <Icon
                :icon="tab.icon"
              />
              {{ tab.iconText }}
            </span>
          </a>
        </li>
      </ul>
    </nav>
    <transition v-bind="transitionProps">
      <component
        :is="selectedTab.component"
        v-if="selectedTab && selectedTab.component"
        :key="selectedTab.title"
        v-bind="selectedTab.componentProps"
        :class="[getContainerStyle(), customContainerStyle]"
      />
    </transition>
  </section>
</template>

<script>
import {
  Icon,
} from '@jumpcloud/ui-components';

import PremiumFeatureBadge from 'client/components/Badges/PremiumFeatureBadge.vue';

export default {
  name: 'TabList',

  components: {
    Icon,
    PremiumFeatureBadge,
  },

  inject: ['$validator'],

  props: {
    activeTab: {
      type: Number,
      default: 0,
    },

    customActiveTabStyle: {
      default: '',
      type: String,
    },

    customContainerStyle: {
      type: [String, Array],
      default: '',
    },

    customNavStyle: {
      type: [String, Array],
      default: '',
    },

    customTabStyle: {
      type: [String, Array],
      default: '',
    },

    iconPosition: {
      type: String,
      default: () => 'left',
    },

    iconText: {
      type: String,
      default: () => '',
    },

    inModal: {
      type: Boolean,
      default: false,
    },

    tabs: {
      type: Array,
      required: true,
    },
  },

  computed: {
    selectedTab() {
      return this.tabs[this.activeTab];
    },

    transitionProps() {
      return {
        enterClass: this.$style.sectionEnter,
        enterActiveClass: this.$style.sectionEnterTransition,
        leaveActiveClass: this.$style.sectionLeaveTransition,
        leaveToClass: this.$style.sectionLeaveTo,
      };
    },
  },

  /**
   * @description If the active tab supplied by the parent component
   * is disabled, find the first enabled tab and set it to active.  If
   * all of the tabs are disabled, then just go with the active tab that
   * is supplied, for lack of a better solution.
   */
  created() {
    const { tabs } = this;
    if (tabs.length && this.isDisabledTab(this.activeTab)) {
      const firstEnabled = tabs.indexOf(tabs.find(tab => !tab.disabled));
      if (firstEnabled !== -1) {
        this.toggleActiveTab(firstEnabled);
      }
    }
  },

  /**
   * @todo At some point check to see if the specified tabs will fit
   * in the parent window. If not, we will need to implement some code to
   * resize, collapse, and/or use ellipses for the Tab titles.
   * @see https://github.com/TheJumpCloud/jumpcloud-admin-portal/blob/master/client/components/AsidePanel/AsidePanel.vue
   *    e.g. `collapseAtClass()`
   */

  methods: {
    getContainerStyle() {
      return this.inModal ? this.$style.transitionModalContainer : this.$style.transitionContainer;
    },

    /**
     * @description Set the header style for the Tab. Checks to see if
     * the Tab is active or disabled and sets the style appropriately.
     */
    getTabStyle(index) {
      const classes = [];

      if (this.isActiveTab(index)) {
        classes.push(this.$style.tabHeaderActive);
      } else if (this.isDisabledTab(index)) {
        classes.push(this.$style.disabled);
      }

      return classes;
    },

    isActiveTab(index) {
      return this.activeTab === index;
    },

    isDisabledTab(index) {
      return this.tabs[index].disabled;
    },

    /**
     * @description Toggles which Tab is currently being displayed.
     */
    toggleActiveTab(index) {
      if (!this.isActiveTab(index) && !this.isDisabledTab(index)) {
        this.$emit('active-tab-change', index);
      }
    },
  },
};
</script>
<style module>
@import './TabList.css';
</style>
